import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import "./App.css";
import "./ui-polish.css";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { AdminProvider } from "./contexts/AdminContext";
import { useTranslation } from "./hooks/useTranslation";
import { useParallax3D } from "./hooks/useParallax3D";
import { useScrollReveal } from "./hooks/useScrollReveal";
import { useAdmin } from "./contexts/AdminContext";
import LanguageSwitcher from "./components/LanguageSwitcher";
import AdminAccess from "./components/AdminAccess";
import AdminDashboard from "./components/AdminDashboard";
import AdminLogin from "./components/AdminLogin";
import ProjectCard from "./components/ProjectCard";
import SkillCategory from "./components/SkillCategory";
import AdditionalSkills from "./components/AdditionalSkills";
import ExperienceItem from "./components/ExperienceItem";
import HeroSpotlight from "./components/HeroSpotlight";
import HeroCursorField from "./components/HeroCursorField";
import HeroCursorFace from "./components/HeroCursorFace";
import { HERO_CURSOR_MODE } from "./config/heroEffects";
import LoadingScreen from "./components/LoadingScreen";
import { trackVisitor } from "./utils/visitorTracker";

// Admin Route Component
function AdminRoute() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <AdminDashboard onClose={() => navigate('/')} />;
  } else {
    return <AdminLogin onClose={() => navigate('/')} onLoginSuccess={() => {}} />;
  }
}

function AppContent() {
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { portfolioData, isAuthenticated, isLoading } = useAdmin();
  // const location = useLocation(); // Removed unused variable

  const getMethodSubtitle = (name) => {
    if (name === "WhatsApp") return t("contact.methodWhatsApp");
    if (name === "Email") return t("contact.methodEmail");
    if (name === "LinkedIn") return t("contact.methodLinkedIn");
    return "";
  };

  // Helper function to safely get array data with fallback
  const getArrayData = (translationKey, fallbackData) => {
    const translation = t(translationKey);
    return translation && Array.isArray(translation)
      ? translation
      : fallbackData;
  };

  // Track visitor on mount
  useEffect(() => {
    trackVisitor();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 24);

      const sections = [
        "home",
        "skills",
        "projects",
        "experience",
        "contact",
      ];
      const scrollPosition = window.scrollY + 100;

      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
          }
        }
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setIsDarkMode(true);
      document.body.classList.add("dark-mode");
    }
  }, []);

  useParallax3D();
  useScrollReveal(!isLoading && !!portfolioData, language, activeCategory);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);

    if (newDarkMode) {
      document.body.classList.add("dark-mode");
      localStorage.setItem("theme", "dark");
    } else {
      document.body.classList.remove("dark-mode");
      localStorage.setItem("theme", "light");
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  const handleCategoryFilter = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const getFilteredProjects = () => {
    // Always use portfolioData for projects to ensure we have all projects
    const projects = portfolioData.projects.items;
    
    if (activeCategory === "all") {
      return projects;
    }
    return projects.filter(project => project.category === activeCategory);
  };

  const getCVLink = () => {
    if (portfolioData && typeof portfolioData.personal?.cv === 'object') {
      return portfolioData.personal.cv[language] || portfolioData.personal.cv.english;
    }
    return portfolioData?.personal?.cv || '#';
  };

  // Show loading state while fetching data
  if (isLoading || !portfolioData) {
    return (
      <LoadingScreen
        message={t("loading.message")}
        subtitle={t("hero.subtitle")}
      />
    );
  }

  const navSections = ["home", "skills", "projects", "experience", "contact"];

  const renderNavActions = (mobile = false) => (
    <div
      className={`nav-actions-v2${mobile ? " nav-actions-v2--mobile" : ""}`}
    >
      <div className="nav-actions-links">
        <a
          href={getCVLink()}
          className="nav-action-btn nav-action-btn-primary"
          target="_blank"
          rel="noopener noreferrer"
          onClick={mobile ? () => setIsMenuOpen(false) : undefined}
        >
          {t("hero.downloadCV")}
        </a>
        <a
          href="https://bit.ly/lajimiportfolio"
          className="nav-action-link nav-action-portfolio"
          target="_blank"
          rel="noopener noreferrer"
          onClick={mobile ? () => setIsMenuOpen(false) : undefined}
        >
          {t("nav.portfolio")}
        </a>
      </div>

      <span className="nav-actions-divider" aria-hidden="true" />

      <div className="nav-actions-utilities">
        <LanguageSwitcher />
        <button
          type="button"
          className="nav-theme-btn"
          onClick={toggleDarkMode}
          aria-label={isDarkMode ? t("nav.light") : t("nav.dark")}
        >
          <i
            className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}
            aria-hidden="true"
          ></i>
        </button>
      </div>

      {isAuthenticated && (
        <button
          type="button"
          className="nav-action-link nav-action-admin"
          onClick={() => {
            setShowDashboard(true);
            if (mobile) setIsMenuOpen(false);
          }}
          aria-label="Open Dashboard"
        >
          Dashboard
        </button>
      )}
    </div>
  );

  return (
    <div className="App">
      <div
        className={`nav-backdrop ${isMenuOpen ? "active" : ""}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Navigation */}
      <nav
        className={`navbar navbar-v2 ${
          isNavScrolled ? "nav-scrolled" : "nav-on-hero"
        }`}
        aria-label="Main navigation"
      >
        <div className="nav-container nav-container-v2">
          <button
            type="button"
            className="nav-brand"
            onClick={() => scrollToSection("home")}
            aria-label={t("nav.home")}
          >
            <span className="nav-brand-first">Mokhles</span>
            <span className="nav-brand-last">Lajimi</span>
          </button>

          <div
            className={`nav-menu nav-menu-v2 ${isMenuOpen ? "active" : ""}`}
            id="nav-menu-panel"
          >
            {navSections.map((section) => (
              <button
                key={section}
                type="button"
                className={`nav-link nav-link-v2 ${
                  activeSection === section ? "active" : ""
                }`}
                onClick={() => scrollToSection(section)}
                aria-current={activeSection === section ? "page" : undefined}
              >
                {t(`nav.${section}`)}
              </button>
            ))}

            <div className="nav-menu-mobile-actions">
              {renderNavActions(true)}
            </div>
          </div>

          <div className="nav-right nav-right-v2">{renderNavActions()}</div>

          <button
            type="button"
            className={`nav-toggle nav-toggle-v2 ${isMenuOpen ? "active" : ""}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            aria-controls="nav-menu-panel"
          >
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero hero-v2">
        <div className="hero-bg hero-parallax-scene" aria-hidden="true">
          {(HERO_CURSOR_MODE === "particles" || HERO_CURSOR_MODE === "both") && (
            <HeroCursorField />
          )}
          <span className="hero-depth-focus" aria-hidden="true" />
          <div className="hero-ambient">
            <span className="hero-interactive-glow" aria-hidden="true" />
            <span className="hero-orb hero-orb-1" />
            <span className="hero-orb hero-orb-2" />
            <span className="hero-orb hero-orb-3" />
            <span className="hero-beam" />
            <span className="hero-grid" />
          </div>
          <div className="floating-shapes">
            <div className="shape shape-1" data-depth="0.35"></div>
            <div className="shape shape-2" data-depth="0.65"></div>
            <div className="shape shape-3" data-depth="0.25"></div>
            <div className="shape shape-4" data-depth="0.5"></div>
          </div>
        </div>

        <div className="hero-content hero-content-v2 hero-depth-stage">
          <div className="hero-text hero-text-v2">
            <div className="hero-badge hero-badge-v2">
              <span className="hero-badge-dot" aria-hidden="true" />
              <span>{t("hero.availableBadge")}</span>
            </div>
            <p className="hero-eyebrow">{t("hero.eyebrow")}</p>
            <h1 className="hero-title hero-title-v2">
              <span className="name-text hero-name-reveal">{t("hero.title")}</span>
              <span className="hero-title-line" aria-hidden="true" />
            </h1>
            <p className="hero-subtitle hero-subtitle-v2">{t("hero.subtitle")}</p>
            <p className="hero-description hero-description-v2">
              {t("hero.description")}
            </p>
            <div className="hero-buttons hero-buttons-v2">
              <a
                href={getCVLink()}
                className="btn btn-primary btn-hero-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-download"></i> {t("hero.downloadCV")}
              </a>
              <a
                href={portfolioData.personal.whatsapp}
                className="btn btn-secondary btn-hero-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-whatsapp"></i> {t("hero.contactWhatsApp")}
              </a>
            </div>
            <div className="hero-trust-row">
              <span>
                <i className="fab fa-google-play" aria-hidden="true"></i>
                Google Play
              </span>
              <span>
                <i className="fab fa-apple" aria-hidden="true"></i>
                App Store
              </span>
              <span>
                <i className="fas fa-globe" aria-hidden="true"></i>
                Web
              </span>
            </div>
          </div>

          {(HERO_CURSOR_MODE === "face" || HERO_CURSOR_MODE === "both") && (
            <div className="hero-face-slot" aria-hidden="true">
              <HeroCursorFace />
            </div>
          )}

          <HeroSpotlight
            recentProjects={portfolioData.projects.items.slice(0, 3)}
            projectCount={portfolioData.projects.items.length}
            onExploreProjects={() => scrollToSection("projects")}
            recentLaunchesLabel={t("hero.recentLaunches")}
            metricsLabels={{
              years: t("hero.metricYears"),
              projects: t("hero.metricProjects"),
              stores: t("hero.metricStores"),
            }}
          />
        </div>

        <button
          type="button"
          className="scroll-indicator scroll-indicator-v2"
          onClick={() => scrollToSection("skills")}
          aria-label="Scroll to skills section"
        >
          <i className="fas fa-chevron-down" aria-hidden="true"></i>
        </button>
      </section>

      {/* Skills Section */}
      <section id="skills" className="skills skills-v2 section-compact">
        <div className="container">
          <div
            className="section-title section-title-compact"
            data-scroll-reveal="section-title"
          >
            <h2>{t("skills.title")}</h2>
            <p>{t("skills.subtitle")}</p>
          </div>

          <div className="skills-board">
            {getArrayData(
              "skills.categories",
              portfolioData.skills.categories
            ).map((skillCategory, index) => (
              <SkillCategory
                key={skillCategory.category}
                skillCategory={skillCategory}
                revealIndex={index}
              />
            ))}
          </div>

          <AdditionalSkills
            title={t("skills.additionalExpertise")}
            subtitle={t("skills.additionalSubtitle")}
            skills={getArrayData(
              "skills.additionalSkills",
              portfolioData.skills.additionalSkills
            )}
          />
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="projects projects-v2 section-compact">
        <div className="container">
          <div
            className="section-title section-title-compact"
            data-scroll-reveal="section-title"
          >
            <h2>{t("projects.title")}</h2>
            <p>{t("projects.subtitle")}</p>
          </div>

          <div className="projects-toolbar" data-scroll-reveal="toolbar">
            <div className="project-categories project-filters-v2">
              {portfolioData.projects.categories.map((category, index) => (
                <button
                  key={category.id}
                  type="button"
                  className={`category-btn ${activeCategory === category.id ? "active" : ""}`}
                  onClick={() => handleCategoryFilter(category.id)}
                  data-category={category.id}
                  style={{ "--filter-index": index }}
                >
                  <i className={category.icon}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
            <p className="project-count-v2" aria-live="polite">
              <strong>{getFilteredProjects().length}</strong>
              <span>{t("projects.countLabel") || "projects"}</span>
            </p>
          </div>

          <div className="projects-grid projects-grid-v2">
            {getFilteredProjects().length === 0 ? (
              <div className="projects-empty">
                <i className="fas fa-folder-open"></i>
                <p>No projects in this category yet.</p>
              </div>
            ) : (
            getFilteredProjects().map((project, index) => (
              <ProjectCard
                key={project.title}
                project={project}
                revealIndex={index}
              />
            ))
            )}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="experience experience-v2 section-compact">
        <div className="container">
          <div
            className="section-title section-title-compact"
            data-scroll-reveal="section-title"
          >
            <h2>{t("experience.title")}</h2>
            <p>{t("experience.subtitle")}</p>
          </div>
          <div className="experience-track">
            {getArrayData(
              "experience.items",
              portfolioData.experience.items
            ).map((experience, index, items) => (
              <ExperienceItem
                key={`${experience.company}-${experience.period}`}
                experience={experience}
                isLast={index === items.length - 1}
                revealIndex={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact contact-v2 section-compact">
        <div className="container">
          <div className="contact-v2-grid">
            <div className="contact-v2-side">
              <div
                className="section-title section-title-compact contact-v2-title"
                data-scroll-reveal="section-title"
              >
                <h2>{t("contact.title")}</h2>
                <p>{t("contact.subtitle")}</p>
              </div>

              <div className="contact-methods contact-methods-v2">
                {getArrayData(
                  "contact.methods",
                  portfolioData.contact.methods
                ).map((method, index) => (
                  <a
                    key={index}
                    href={method.url}
                    className={`contact-method contact-method-v2 contact-method-${method.name.toLowerCase()}`}
                    data-scroll-reveal="contact-link"
                    style={{ "--reveal-delay": `${0.12 + index * 0.1}s` }}
                    target={method.url.startsWith("http") ? "_blank" : "_self"}
                    rel={
                      method.url.startsWith("http") ? "noopener noreferrer" : ""
                    }
                  >
                    <span className="contact-method-icon" aria-hidden="true">
                      <i className={method.icon}></i>
                    </span>
                    <span className="contact-method-text">
                      <strong>{method.name}</strong>
                      <span>{getMethodSubtitle(method.name)}</span>
                    </span>
                  </a>
                ))}
              </div>

              <p
                className="contact-v2-note"
                data-scroll-reveal="contact-note"
                style={{ "--reveal-delay": "0.42s" }}
              >
                <i className="fas fa-clock" aria-hidden="true"></i>
                {t("contact.form.hint")}
              </p>
            </div>

            <form
              className="contact-form contact-form-v2"
              data-scroll-reveal="contact-form"
              style={{ "--reveal-delay": "0.08s" }}
              onSubmit={async (e) => {
                e.preventDefault();
                setIsSubmitting(true);
                const formData = new FormData(e.target);
                const data = {
                  name: formData.get('name'),
                  email: formData.get('email'),
                  company: formData.get('company'),
                  message: formData.get('message'),
                  timestamp: new Date(),
                  read: false
                };
                
                try {
                  const { collection, addDoc } = await import('firebase/firestore');
                  const { db } = await import('./config/firebase');
                  await addDoc(collection(db, 'contactMessages'), data);
                  alert('Message sent successfully! Thank you for contacting me.');
                  e.target.reset();
                } catch (error) {
                  console.error('Error sending message:', error);
                  alert('Failed to send message. Please try again or contact me directly via email.');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">
                    {t("contact.form.fields")[0].label}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t("contact.form.fields")[0].placeholder}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    {t("contact.form.fields")[1].label}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t("contact.form.fields")[1].placeholder}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="company">
                  {t("contact.form.fields")[2].label}
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  placeholder={t("contact.form.fields")[2].placeholder}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  {t("contact.form.fields")[3].label}
                </label>
                <textarea
                  id="message"
                  name="message"
                  placeholder={t("contact.form.fields")[3].placeholder}
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary contact-submit-v2" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Sending...
                  </>
                ) : (
                  <>
                    <i className="fa-regular fa-paper-plane"></i> {t("contact.form.submitText")}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer footer-v2">
        <div className="container footer-v2-inner">
          <div className="footer-top-v2">
            <div className="footer-brand-block">
              <button
                type="button"
                className="footer-brand-wordmark"
                onClick={() => scrollToSection("home")}
              >
                <span className="footer-brand-first">Mokhles</span>
                <span className="footer-brand-last">Lajimi</span>
              </button>
              <p className="footer-brand-desc">{t("footer.description")}</p>
            </div>

            <nav className="footer-nav-inline" aria-label="Footer navigation">
              {getArrayData(
                "footer.quickLinks",
                portfolioData.footer.quickLinks
              ).map((link, index) =>
                link.action === "scroll" ? (
                  <button
                    key={index}
                    type="button"
                    className="footer-nav-link"
                    onClick={() => scrollToSection(link.target)}
                  >
                    {link.name}
                  </button>
                ) : (
                  <a
                    key={index}
                    className="footer-nav-link"
                    href={
                      link.target === "dynamic_cv" ? getCVLink() : link.target
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.name}
                  </a>
                )
              )}
            </nav>

            <div className="footer-social-inline">
              {getArrayData(
                "footer.socialLinks",
                portfolioData.footer.socialLinks
              ).map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  className="footer-social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>

          <div className="footer-bottom-v2">
            <p>{t("footer.copyright")}</p>
          </div>
        </div>
      </footer>

      {/* Admin Access */}
      <AdminAccess />

      {/* Dashboard from Navigation */}
      {showDashboard && isAuthenticated && (
        <AdminDashboard onClose={() => setShowDashboard(false)} />
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AdminProvider>
          <Routes>
            <Route path="/mokhles@dash" element={<AdminRoute />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </AdminProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
